import React, { useState } from "react";
import { BookOpen, Search } from "lucide-react";
import { ReactComponent as PathfinderLogoBlue } from "@/assets/images/Pathfinder logo_Blue.svg";
import { Link, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/global/base/dropdown-menu";
import NavBarButton, { navLinkTestId } from "./NavBarButton";
import useRoles from "@/hooks/useRoles";
import { getVisibleNavItems, PERMISSION_NAV_ITEMS } from "@/shared/navFeatures";
import { App, Auth } from "@/routes/Constants";
import { prescriberName } from "@/shared/helpers";
import APIVariables from "@/services/api-variables";
import Axios from "@/services/Axios";
import LocalStorage from "@/shared/localStorage";

/** @param {string} text */
const navMenuItemTestId = (text) => {
  const slug = String(text || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug || "item"}-menu-item`;
};

const NavBar = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const {
    isPrescriber,
    isPharmacy,
    isConnectedNurse,
    isHomecare,
    isOutpatient,
    isCustomerSolution,
    isPrimaryPharmacist,
    canManagePatients,
    storageData,
  } = useRoles();

  const permissionNavItems = getVisibleNavItems(
    PERMISSION_NAV_ITEMS,
    storageData
  );

  const signOut = async () => {
    try {
      await Axios.get(APIVariables.USERS_LOGOUT);
    } catch {
      // continue to clear and redirect
    }
    localStorage.removeItem(LocalStorage.USER_NHSPORTAL);
    window.location.href = Auth.LOGIN;
  };

  const redirect = (url) => {
    const id =
      storageData?.registration_number ||
      storageData?.pharmacy?.registration_number;
    if (url === App.PROFILE_DEFAULT) {
      navigate(App.PROFILE_DEFAULT + id);
    } else {
      navigate(url);
    }
  };

  const homeUrl = isCustomerSolution ? App.PHARMACY_LIST : App.DASHBOARD;

  const showPatientSearch = canManagePatients;

  const submitNavSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q || !showPatientSearch) return;
    navigate(`${App.VIEW_PATIENTS}?search=${encodeURIComponent(q)}`);
    setSearchQuery("");
  };

  const showMainNav =
    storageData?.isLoggedIn &&
    (isPrescriber ||
      isPharmacy ||
      isConnectedNurse ||
      isHomecare ||
      isOutpatient ||
      isCustomerSolution);

  if (!showMainNav) return null;

  const isHcpPatientNav =
    isPrescriber ||
    isPharmacy ||
    isConnectedNurse ||
    isHomecare ||
    isOutpatient;

  const renderNavLink = (item) => (
    <NavBarButton
      key={item.id}
      text={item.label}
      url={item.url}
      data-testid={navLinkTestId(item.label)}
    />
  );

  const renderMobileNavLink = (item) => (
    <li key={item.id}>
      <DropdownMenuItem
        className="cursor-pointer px-4 py-2"
        onSelect={() => navigate(item.url)}
        data-testid={navMenuItemTestId(item.label)}
      >
        {item.label}
      </DropdownMenuItem>
    </li>
  );

  const patientSearchForm = showPatientSearch && (
    <form
      onSubmit={submitNavSearch}
      autoComplete="off"
      className="ml-1 flex h-full shrink-0 items-center gap-2 rounded-full border border-gray-200/70 bg-white px-3 py-1 shadow-sm"
      role="search"
      data-testid="navbar-search-form"
    >
      <input
        type="search"
        name="navbar-search"
        autoComplete="off"
        placeholder="Search"
        value={searchQuery}
        onChange={(ev) => setSearchQuery(ev.target.value)}
        className="w-20 min-w-0 bg-transparent text-sm font-medium text-[#546e7a] placeholder:!text-sm placeholder:!text-[#546e7a] placeholder:opacity-100 placeholder:font-medium outline-none sm:w-28"
        aria-label="Search patients"
        data-testid="navbar-search-input"
      />
      <button
        type="submit"
        className="text-[#546e7a] transition-colors hover:text-[#37474f]"
        aria-label="Submit search"
        data-testid="navbar-search-submit"
      >
        <Search className="h-4 w-4" />
      </button>
    </form>
  );

  return (
    <nav
      className="border-b border-gray-200/80 bg-[#f8f9fa] font-sans"
      data-testid="navbar"
    >
      <div className="mx-auto flex items-center justify-between gap-3 px-8 py-3 sm:gap-6">
        <Link
          to={homeUrl}
          className="flex shrink-0 items-center transition-opacity hover:opacity-90"
          aria-label="Pathfinder home"
          data-testid="navbar-logo-link"
        >
          <PathfinderLogoBlue className="h-7 w-auto" aria-hidden />
        </Link>

        <div className="hidden min-[1250px]:flex min-w-0 flex-1 justify-end overflow-x-auto overflow-y-hidden">
          <div className="flex w-max flex-nowrap items-center justify-end gap-0.5 rounded-full border border-gray-200/90 bg-[#eceff1] px-1 py-1">
            <NavBarButton text="Home" url={homeUrl} />
            {isPrescriber && (
              <NavBarButton
                text="Refer Patient"
                url={App.PATIENT_INITIATION}
              />
            )}
            {canManagePatients && (
              <NavBarButton text="Manage Patients" url={App.VIEW_PATIENTS} />
            )}
            {/* {isHcpPatientNav && <NavBarButton text="Data" url={App.DATA} />} */}
            {isHcpPatientNav && (
              <NavBarButton text="Notifications" url={App.NOTIFICATIONS} />
            )}
            {permissionNavItems.map(renderNavLink)}
            {patientSearchForm}
          </div>
        </div>

        <div className="shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="navbar-menu-button"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#e3f2fd] px-4 py-2 text-sm font-medium text-[#1976d2] transition-colors hover:bg-[#bbdefb] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1976d2]/35 focus-visible:ring-offset-2 focus-visible:ring-offset-[#f8f9fa] sm:px-5"
              >
                <BookOpen className="h-4 w-4 shrink-0" aria-hidden />
                Menu
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="mr-2 mt-1 max-h-[min(85vh,32rem)] min-w-[12rem] overflow-y-auto p-0">
              <ul className="min-[1250px]:hidden py-1" data-testid="navbar-mobile-nav">
                <li>
                  <DropdownMenuItem
                    className="cursor-pointer px-4 py-2"
                    onSelect={() => navigate(homeUrl)}
                    data-testid={navMenuItemTestId("Home")}
                  >
                    Home
                  </DropdownMenuItem>
                </li>
                {isPrescriber && (
                  <li>
                    <DropdownMenuItem
                      className="cursor-pointer px-4 py-2"
                      onSelect={() => navigate(App.PATIENT_INITIATION)}
                      data-testid={navMenuItemTestId("Refer Patient")}
                    >
                      Refer Patient
                    </DropdownMenuItem>
                  </li>
                )}
                {canManagePatients && (
                  <li>
                    <DropdownMenuItem
                      className="cursor-pointer px-4 py-2"
                      onSelect={() => navigate(App.VIEW_PATIENTS)}
                      data-testid={navMenuItemTestId("Manage Patients")}
                    >
                      Manage Patients
                    </DropdownMenuItem>
                  </li>
                )}
                {isHcpPatientNav && (
                  <li>
                    <DropdownMenuItem
                      className="cursor-pointer px-4 py-2"
                      onSelect={() => navigate(App.DATA)}
                      data-testid={navMenuItemTestId("Data")}
                    >
                      Data
                    </DropdownMenuItem>
                  </li>
                )}
                {isHcpPatientNav && (
                  <li>
                    <DropdownMenuItem
                      className="cursor-pointer px-4 py-2"
                      onSelect={() => navigate(App.NOTIFICATIONS)}
                      data-testid={navMenuItemTestId("Notifications")}
                    >
                      Notifications
                    </DropdownMenuItem>
                  </li>
                )}
                {permissionNavItems.map(renderMobileNavLink)}
              </ul>
              <DropdownMenuSeparator className="min-[1250px]:hidden" />
              <ul className="py-1" data-testid="navbar-account-menu">
                {(isPrescriber ||
                  isPharmacy ||
                  isConnectedNurse ||
                  isHomecare ||
                  isOutpatient) && (
                    <li>
                      <DropdownMenuItem
                        className="cursor-pointer px-4 py-2"
                        onSelect={() => redirect(App.PROFILE_DEFAULT)}
                        data-testid="profile-menu-item"
                      >
                        {prescriberName()}
                        <span className="block text-xs text-muted-foreground">
                          View Profile
                        </span>
                      </DropdownMenuItem>
                    </li>
                  )}
                {isPrescriber && (
                  <li>
                    <DropdownMenuItem
                      className="cursor-pointer px-4 py-2"
                      onSelect={() => redirect(App.NURSES)}
                      data-testid={navMenuItemTestId("Connected Nurses")}
                    >
                      Connected Nurses
                    </DropdownMenuItem>
                  </li>
                )}
                {(isPharmacy || isHomecare || isOutpatient) &&
                  isPrimaryPharmacist && (
                    <li>
                      <DropdownMenuItem
                        className="cursor-pointer px-4 py-2"
                        onSelect={() => redirect(App.CONNECTED_PHARMACISTS)}
                        data-testid={navMenuItemTestId("Connected Pharmacist")}
                      >
                        Connected Pharmacist
                      </DropdownMenuItem>
                    </li>
                  )}
                {isPharmacy && (
                  <li>
                    <DropdownMenuItem
                      className="cursor-pointer px-4 py-2"
                      onSelect={() => redirect(App.CONNECTED_OUTPATIENT)}
                      data-testid={navMenuItemTestId("Connected Outpatient")}
                    >
                      Connected Outpatient
                    </DropdownMenuItem>
                  </li>
                )}
                {isPharmacy && (
                  <li>
                    <DropdownMenuItem
                      className="cursor-pointer px-4 py-2"
                      onSelect={() => redirect(App.HOMECARE)}
                      data-testid={navMenuItemTestId("Connected Homecare")}
                    >
                      Connected Homecare
                    </DropdownMenuItem>
                  </li>
                )}
                {(isPrescriber ||
                  isPharmacy ||
                  isConnectedNurse ||
                  isHomecare ||
                  isOutpatient) && (
                    <li>
                      <DropdownMenuItem
                        className="cursor-pointer px-4 py-2"
                        onSelect={() => redirect(App.PATIENT_MATERIALS)}
                        data-testid={navMenuItemTestId("HCP and Patient Material")}
                      >
                        HCP & Patient Material
                      </DropdownMenuItem>
                    </li>
                  )}
                {(isPrescriber ||
                  isPharmacy ||
                  isConnectedNurse ||
                  isHomecare ||
                  isOutpatient) && (
                    <li>
                      <DropdownMenuItem
                        className="cursor-pointer px-4 py-2"
                        onSelect={() => redirect(App.REPORT_EVENT)}
                        data-testid={navMenuItemTestId("Report an AE Pregnancy")}
                      >
                        Report an AE/Pregnancy
                      </DropdownMenuItem>
                    </li>
                  )}
                {isCustomerSolution && (
                  <li>
                    <DropdownMenuItem
                      className="cursor-pointer px-4 py-2"
                      onSelect={() => redirect(App.PHARMACY_LIST)}
                      data-testid={navMenuItemTestId("Registered Pharmacy Listing")}
                    >
                      Registered Pharmacy Listing
                    </DropdownMenuItem>
                  </li>
                )}
                {!isCustomerSolution && (
                  <li>
                    <DropdownMenuItem
                      className="cursor-pointer px-4 py-2"
                      onSelect={() => redirect(App.CONTACT_US)}
                      data-testid={navMenuItemTestId("Contact Us")}
                    >
                      Contact Us
                    </DropdownMenuItem>
                  </li>
                )}
                <li>
                  <DropdownMenuItem
                    className="cursor-pointer px-4 py-2 text-red-600 focus:text-red-600"
                    onSelect={() => signOut()}
                    data-testid="logout-menu-item"
                  >
                    Log Out
                  </DropdownMenuItem>
                </li>
              </ul>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
